<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h4>{{ title }}</h4>
        <!-- Close Button -->
        <button class="btn btn-light close-btn" @click="closeModal">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="modal-body">
        <p v-if="identifierLabel"><strong>{{ identifierLabel }}:</strong> {{ identifierValue }}</p>
        <p v-if="messageLabel"><strong>{{ messageLabel }}:</strong> {{ messageValue }}</p>
        <p v-if="statusLabel">
          <strong>{{ statusLabel }}:&nbsp;</strong>
          <span :class="statusClass(statusValue)">{{ statusValue }}</span>
        </p>
        <p v-if="dateTimeLabel"><strong>{{ dateTimeLabel }}:</strong> {{ dateTimeValue }}</p>
        <p v-if="delarnameLabel"><strong>{{ delarnameLabel }}:</strong> {{ delarnameValue }}</p>

        <!-- Show details of the complaint when it is pending -->
        <div v-if="statusValue.toLowerCase() === 'pending'">
          <p><strong>Reply :</strong></p>
          <textarea
              id="reply"
              v-model="replyMessage"
              placeholder="Type your message here..."
              :disabled="isReplyDisabled"
              maxlength="200"
              @keydown.enter="sendReply"
          >
          </textarea>
          <!-- Character count display -->
          <p class="text-muted small mt-1">
            {{ replyMessage.length }}/200
          </p>
        </div>

        <!--Show details of the complaint when it in-progress-->
        <div v-if="histories && histories.length && statusValue.toLowerCase() === 'inprogress'">
          <div v-for="(history, index) in histories" :key="index">
            <p><strong>Reply :</strong> {{ history.replyValue }}</p>
            <p><strong>Reply Sent by:</strong> {{ history.replySentByValue }}</p>
            <p><strong>Replied Date & Time:</strong> {{ history.replyDateTimeValue }}</p>
          </div>
        </div>

        <!--Show details of the complaint when it completed-->
        <div v-if="histories && histories.length && statusValue.toLowerCase() === 'completed'">
          <div v-for="(history, index) in histories" :key="index">
            <p><strong>Reply :</strong> {{ history.replyValue }}</p>
            <p><strong>Reply Sent by:</strong> {{ history.replySentByValue }}</p>
            <p><strong>Replied Date & Time:</strong> {{ history.replyDateTimeValue }}</p>
            <p><strong>Completed by:</strong> {{ history.completedBy }}</p>
            <p><strong>Completed Date & Time:</strong> {{ history.completedDateTime }}</p>
          </div>
        </div>
        <input type="hidden" :value="complaintId" />
      </div>

      <div class="modal-footer">
        <div v-if="statusValue.toLowerCase() === 'pending'">
          <button @click="sendReply" class="btn btn-success" :disabled="loading">
            <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> Sending...</span>
            <span v-else>Send</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="loading" class="loading-spinner">
    <i class="fa fa-spinner fa-spin"></i> Loading...
  </div>
</template>

<script>
import { updateComplaints } from '@/services/dataService';
import { useToast } from 'vue-toastification';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'View Details',
    },
    identifierLabel: {
      type: String,
      default: 'Identifier',
    },
    identifierValue: String,

    messageLabel: {
      type: String,
      default: 'Message',
    },
    messageValue: String,

    replyLabel: {
      type: String,
      default: 'Reply',
    },
    replyValue: String,

    replySentByLabel: {
      type: String,
      default: 'ReplySentBy',
    },
    replySentByValue: String,

    replyDateTimeLabel: {
      type: String,
      default: 'ReplySentBy',
    },
    replyDateTimeValue: String,

    completedByLabel: {
      type: String,
      default: 'Complaint',
    },
    completedByValue: String,

    completedDateTimeLabel: {
      type: String,
      default: 'CompletedDateTime',
    },
    completedDateTime: String,

    statusLabel: {
      type: String,
      default: 'Status',
    },
    statusValue: String,
    dateTimeLabel: {
      type: String,
      default: 'Complaint Sent Date & Time',
    },
    dateTimeValue: String,
    businessNameLabel: {
      type: String,
      default: 'Dealer Name',
    },
    delarnameLabel: {
      type: String,
      default: 'Dealer Name',
    },
    delarnameValue: String,
    histories: Array,
    businessNameValue: String,
    profileId: Number,
    complaintId: Number,
    token: String
  },

  data() {
    return {
      replyMessage: '',
      profileId: null,
      showSuccessMessage: false,
      loading: false,
    };
  },

  mounted() {
    this.profileId = localStorage.getItem('profile_id');
  },

  computed: {
    isReplyDisabled() {
      return this.showSuccessMessage;
    },
  },

  methods: {
    closeModal() {
      this.$emit('close');
      this.replyMessage = '';
    },
    closeSuccessMessage() {
      this.showSuccessMessage = false;
      this.refreshPage();
    },
    refreshPage() {
      window.location.reload();
    },
    async sendReply() {
      const toast = useToast();

      if (!this.replyMessage) {
        toast.error('Reply message cannot be empty', {
          position: "top-right",
          timeout: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        });
        return;
      }

      this.loading = true;
      const token = localStorage.getItem('token_set') || '';

      const params = {
        profile_id: this.profileId,
        complaint_id: this.complaintId,
        replay: this.replyMessage,
      };

      try {
        const response = await updateComplaints(params, token);
        if (response.status === 200) {
          toast.success('Reply sent successfully!', {
            position: "top-right",
            timeout: 5000,
            closeOnClick: true,
            pauseOnHover: true,
          });
          this.$emit('close');
          this.replyMessage = "";
          window.location.reload(); // Refresh the page
        } else {
          toast.error('Failed to send reply', {
            position: "top-right",
            timeout: 5000,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      } catch (error) {
        toast.error('Error occurred while sending reply', {
          position: "top-right",
          timeout: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        });
      } finally {
        this.loading = false;
      }
    },
    //Show status badge
    statusClass(status) {
      const normalizedStatus = status.toLowerCase();

      if (normalizedStatus === 'inprogress') return 'badge badge-inprogress' // No badge class was defined for 'inprogress', can be added later if needed.
      if (normalizedStatus === 'pending') return 'badge badge-pending'; // #ff9800
      if (normalizedStatus === 'completed') return 'badge badge-completed'; // #28a745

      return 'badge badge-secondary'; // Default fallback
    },
  },
};
</script>

<style scoped>
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 500px;
  max-width: 80%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

/* Close Button */
.close-btn {
  border-radius: 8px; /* Makes the button circular */
  width: 33px; /* Set a fixed width */
  height: 33px; /* Match the tab height */
  padding: 0;
  cursor: pointer;
}

.modal-body p {
  margin: 10px 0;
}

textarea {
  width: 100%;
  min-height: 100px;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
  outline: none;
  transition: border-color 0.3s;
  resize: none; /* Disable resizing */
}

textarea:focus {
  border: 2px solid #ccc;
}

textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.small {
  display: block;
  text-align: left;
  margin-left: 5px;
  color: #ccc;
}

.badge-pending {
  background-color: #ff9800;
  font-size: 12px;
}

.badge-inprogress {
  background-color: #007bff;
}

.badge-completed{
  background-color: #28a745;
}
</style>